// ============================================================
// lazyWithPreload — React.lazy with a preload() that lets the FIRST
// render skip Suspense.
//
// Why: the marketing pages ship as ready-made HTML (see
// scripts/prerender-marketing.js), so a visitor sees the real page before
// the JavaScript runs. React then replaces that HTML on its first render.
// With plain React.lazy the first render would be the Suspense fallback
// (the loading screen), so the page would flash: real content, spinner,
// real content. index.js calls preload() for the current path BEFORE it
// renders, and a component whose module is already loaded renders it
// directly, so the swap is invisible.
//
// The choice between "loaded module" and "lazy" is made ONCE per mount
// (useState initialiser). Deciding on every render would switch element
// types the first time a later render saw the module loaded, remounting
// the page and throwing away its state.
// ============================================================

import React, { lazy, useState } from 'react';

export default function lazyWithPreload(factory) {
  let loaded = null;
  let promise = null;

  const load = () => {
    if (!promise) {
      promise = factory().then((mod) => {
        loaded = mod;
        return mod;
      });
    }
    return promise;
  };

  const LazyComponent = lazy(load);

  function Preloadable(props) {
    const [mod] = useState(() => loaded);
    if (mod) {
      const Component = mod.default;
      return <Component {...props} />;
    }
    return <LazyComponent {...props} />;
  }

  Preloadable.preload = load;
  return Preloadable;
}
